import type React from "react";
import { C_SURFACE } from "@schaeffler-commerce/design-system";
import { type Chart as ChartJS, type TooltipItem } from "chart.js/auto";
import type {
  FilterOptionDTO,
  PerformanceGapsDataDTO,
  RawFilterOptionDTO,
  CustomerPerformanceDashboardData,
  YearlyDataDTO,
} from "~/features/cms/homepagePersonalization/cpd/CPDTypes";
import type { TFunction } from "i18next";
import { getRuntimeConfig } from "~/common/config/runtimeConfig";
import dayjs from "dayjs";

const defaultPerformanceGapsData = {
  currency: getRuntimeConfig().currencyIso,
  target: [],
  invoice: [],
  orderbook: [],
  gap: [],
  cashback: [],
  bonus: [],
  filterOptions: [],
  lastUpdatedDate: "",
};

const defaultyearlyData = {
  currency: getRuntimeConfig().currencyIso,
  invoice: [],
  orderbook: [],
  lastUpdatedDate: "",
};

export const transformedCustomerPerformanceDashboardData = (
  t: TFunction,
  data: CustomerPerformanceDashboardData | null | undefined,
) => {

  if (!data) {
    return {
      yearly_data: defaultyearlyData,
      performanceGapsData: defaultPerformanceGapsData,
    };
  }

  const customerPerformanceGapsData = data.performanceGapsData;
  const transformedFilterOptions: FilterOptionDTO[] =
    customerPerformanceGapsData.filterOptions.map((option: RawFilterOptionDTO, index: number) => ({
      value: option.yearlyTarget.toString(),
      label: t("cpd.graph1.filter.targetLabel", { index: index + 1 }),
      key: option.key,
      yearlyTarget: option.yearlyTarget,
      gapYearlyTarget: option.gapYearlyTarget,
      bonusTarget: option.bonusTarget,
    })) || [];

  const performanceGapsData: PerformanceGapsDataDTO = {
    currency: customerPerformanceGapsData.currency,
    target: normalizeValues(customerPerformanceGapsData.target || []),
    invoice: normalizeValues(customerPerformanceGapsData.invoice || []),
    orderbook: normalizeOrderbook(customerPerformanceGapsData.orderbook || []),
    gap: normalizeValues(customerPerformanceGapsData.gap || []),
    cashback: customerPerformanceGapsData.cashback || [],
    bonus: customerPerformanceGapsData.bonus || [],
    filterOptions: transformedFilterOptions,
    lastUpdatedDate: customerPerformanceGapsData.lastUpdatedDate,
  };

  const yearlyData: YearlyDataDTO = {
    currency: data.yearlyData.currency,
    invoice: normalizeValues(data.yearlyData.invoice || []),
    orderbook: normalizeValues(data.yearlyData.orderbook || []),
    lastUpdatedDate: customerPerformanceGapsData.lastUpdatedDate,
  };

  return {
    yearly_data: yearlyData || defaultyearlyData,
    performanceGapsData: performanceGapsData || defaultPerformanceGapsData,
  };
};

function normalizeValues(values: number[]): (number | null)[] {
  return values.map((value) => (value == 0 ? null : value));
}

function normalizeOrderbook(values: number[]): (number | null)[] {
  const currentMonth = dayjs().month() + 1;
  const currentQuarter = Math.floor((currentMonth - 1) / 3) + 1; // 1-based quarter

  return values.map((value, index) => {
    if (index >= currentQuarter - 1) {
      return value == 0 ? null : value;
    }
    return null;
  });
}

export function findFirstNonZeroGap(data: PerformanceGapsDataDTO): { value: FilterOptionDTO; index: number } | null {
  const targets = data?.filterOptions;
  if (!targets?.length) {
    return null;
  }

  const index = targets.findIndex((item) => item.gapYearlyTarget > 0);

  if (index !== -1) {
    return { value: targets[index], index };
  }

  return targets.length > 0 ? { value: targets[targets.length - 1], index: targets.length - 1 } : null;
}

export const createTooltipElement = (tooltipElRef: React.MutableRefObject<HTMLDivElement | null>) => {
  let tooltipEl = tooltipElRef.current;
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.style.position = "absolute";
    tooltipEl.style.zIndex = "100";
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.borderRadius = "8px";
    tooltipEl.style.backgroundColor = C_SURFACE;
    tooltipEl.style.filter = "drop-shadow(0px 2px 6px rgba(0, 0, 0, 0.15)) drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.3))";

    document.body.appendChild(tooltipEl);
    tooltipElRef.current = tooltipEl;
  }
  return tooltipEl;
};

export const createCaret = (tooltipEl: HTMLDivElement) => {
  if (!tooltipEl.querySelector(".tooltip-caret")) {
    const caret = document.createElement("div");
    caret.className = "tooltip-caret";
    caret.style.position = "absolute";
    caret.style.bottom = "-6px";
    caret.style.left = "50%";
    caret.style.transform = "translateX(-50%)";
    caret.style.width = "0";
    caret.style.height = "0";
    caret.style.borderLeft = "6px solid transparent";
    caret.style.borderRight = "6px solid transparent";
    caret.style.borderTop = `6px solid ${C_SURFACE}`;

    tooltipEl.appendChild(caret);
  }
};

export const positionTooltip = (tooltipEl: HTMLDivElement, chart: ChartJS, dataPoint: TooltipItem<"bar">) => {
  requestAnimationFrame(() => {
    const position = chart.canvas.getBoundingClientRect();
    const barTopY = dataPoint.element.y;
    const barX = dataPoint.element.x;

    tooltipEl.style.opacity = "1";
    tooltipEl.style.left = position.left + window.pageXOffset + barX + "px";
    tooltipEl.style.top = position.top + window.pageYOffset + barTopY - tooltipEl.offsetHeight - 10 + "px";
    tooltipEl.style.transform = "translateX(-50%)";
  });
};

export const commonChartStyleProps = (color: string) => {
  return {
    backgroundColor: color,
    hoverBackgroundColor: color,
    hoverBorderColor: color,
    borderRadius: 4,
  };
};
